import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, firstValueFrom, Observable, Subject, tap } from 'rxjs';
import { User } from 'src/app/shared/model/user';
import { ApiService } from 'src/app/shared/service/http.service';
import { environment } from 'src/environments/environment';
import { MessageDto } from '../model/messages/message';
import { MessageRoomDto } from '../model/messages/message-room.dto';

@Injectable({
  providedIn: 'root',
})
export class RoomService {
  chatUsersSuggestions = new Subject<Partial<User>[]>();

  private roomRoutes = environment.api.messages;
  private roomsSubject = new BehaviorSubject<MessageRoomDto[]>([]);
  public rooms$ = this.roomsSubject.asObservable();
  private roomSingleSubject = new Map<number, BehaviorSubject<MessageRoomDto>>();
  roomCreated$ = new Subject<MessageRoomDto>();
  callInitiated$ = new Subject<{ id: number; fromUserId: number; toUserId: number; fromUser: Partial<User> }>();
  callDeclined$ = new Subject<{ id: number; fromUserId: number; toUserId: number }>();
  callAccepted$ = new Subject<{ id: number; fromUserId: number; toUserId: number; startedAt: string }>();
  callEnded$ = new Subject<{ id: number; fromUserId: number; toUserId: number }>();
  callSignal$ = new Subject<{ callId: number; fromUserId: number; type: string; payload: string }>();
  private hubConnection?: signalR.HubConnection;


  constructor(private api: ApiService, private router: Router) {}

  startHub(token: string, hubUrl: string = environment.hub.notification): Promise<void> {
    if (
      this.hubConnection &&
      this.hubConnection.state !== signalR.HubConnectionState.Disconnected
    ) {
      return Promise.resolve();
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { accessTokenFactory: () => token })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.registerHubEvents();

    return this.hubConnection.start().catch((err) => {
      console.error('Failed to start hub:', err);
      throw err;
    });
  }

  sendCallSignal(callId: number, toUserId: number, type: string, payload: string): Promise<void> {
    if (!this.hubConnection) return Promise.reject(new Error('Hub is not connected'));
    return this.hubConnection.invoke('SendCallSignal', callId, toUserId, type, payload);
  }

  stopHub(): Promise<void> {
    if (!this.hubConnection) return Promise.resolve();
    return this.hubConnection.stop().catch(console.error);
  }

  private registerHubEvents() {
    if (!this.hubConnection) return;

    this.hubConnection.on('NewRoomCreated', (room: MessageRoomDto) => {
      this.addRoom(room);
    });

    this.hubConnection.on('RoomRenamed', (roomId: number, newName: string) => {
      this.renameRoom(roomId, newName);
    });

    this.hubConnection.on('CallInitiated', (_callId: number, _fromUserId: number, call: { id: number; fromUserId: number; toUserId: number; fromUser: Partial<User> }) => {
      this.callInitiated$.next(call);
    });

    this.hubConnection.on('CallDeclined', (_callId: number, _userId: number, call: { id: number; fromUserId: number; toUserId: number }) => {
      this.callDeclined$.next(call);
    });

    this.hubConnection.on('CallAccepted', (_callId: number, _userId: number, call: { id: number; fromUserId: number; toUserId: number; startedAt: string }) => {
      this.callAccepted$.next(call);
    });

    this.hubConnection.on('CallEnded', (_callId: number, _userId: number, call: { id: number; fromUserId: number; toUserId: number }) => {
      this.callEnded$.next(call);
    });

    this.hubConnection.on('CallSignal', (callId: number, fromUserId: number, type: string, payload: string) => {
      this.callSignal$.next({ callId, fromUserId, type, payload });
    });
  }


  returnObservableChatUsers(user?: string): Observable<Partial<User>[]> {
    const searchText = user ? user : 'null';
    return this.api.Get(this.roomRoutes.userSuggestions, {
      params: { searchText },
    });
  }

  getChatUsersSuggestions(user?: string) {
    const searchText = user ? user : 'null';
    this.api
      .Get<Partial<User>[]>(this.roomRoutes.userSuggestions, {
        params: { searchText },
      })
      .subscribe((res) => {
        this.chatUsersSuggestions.next(res);
      });
  }

  async startPrivateChat(obj: number): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.api.Post<MessageRoomDto>(this.roomRoutes.room.privateChat, {
          participantId: obj,
        }),
      );

      this.navigateToRoom(res.id);
    } catch (err) {
      console.error('Failed to start chat:', err);
    }
  }

  async getPrivateroomId(obj:number): Promise<number |undefined>{
    try{
      const res = await firstValueFrom(this.api.Post<MessageRoomDto>(this.roomRoutes.room.privateChat,{
        participantId:obj,
      }),
    ); 
    return res.id;
  }
    catch (err){
      console.error('Failed to start chat:', err);
      return undefined;
    }
  }

  async createGroupChat(groupName: string, userIds: number[]): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.api.Post<MessageRoomDto>(this.roomRoutes.room.createGroupRoom, {
          name: groupName,
          participantIds: userIds,
        }),
      );
      this.updateRoomState(res);

      this.navigateToGroupRoom(res.id);
    } catch (err) {
      console.error('Failed to create group chat:', err);
    }
  }

  addUserToGroup(roomId: number, userId: number): Observable<MessageRoomDto> {
    return this.api.Post<MessageRoomDto>(this.roomRoutes.room.addUserToGroup, {
      roomId,
      userId,
    });
  }

  removeUserFromGroup(
    roomId: number,
    userId: number,
    adminId?: number,
  ): Observable<MessageRoomDto> {
    let params = new HttpParams()
      .append('roomId', roomId.toString())
      .append('userId', userId.toString());

    if (adminId != null) params = params.append('adminId', adminId.toString());

    return this.api.Delete<MessageRoomDto>(this.roomRoutes.room.removeUserFromGroup, { params });
  }

  renameGroup(roomId: number, newName: string): Observable<MessageRoomDto> {
    return this.api.Put<MessageRoomDto>(this.roomRoutes.room.renameGroup, {
      roomId,
      name: newName,
    });
  }

  getRoomById(roomId: number): Observable<MessageRoomDto> {
    return this.api.Get<MessageRoomDto>(this.roomRoutes.room.byId, {
      params: { roomId },
    });
  }

  getRoomObservable(roomId: number): Observable<MessageRoomDto> {
    if (!this.roomSingleSubject.has(roomId)) {
      this.roomSingleSubject.set(roomId, new BehaviorSubject<MessageRoomDto>({} as MessageRoomDto));

      this.getRoomById(roomId).subscribe({
        next: (room) => this.roomSingleSubject.get(roomId)?.next(room),
        error: console.error,
      });
    }
    return this.roomSingleSubject.get(roomId)!.asObservable();
  }

  updateRoomState(room: MessageRoomDto) {
    let subject = this.roomSingleSubject.get(room.id);

    if (!subject) {
      subject = new BehaviorSubject<MessageRoomDto>(room);
      this.roomSingleSubject.set(room.id, subject);
    } else {
      subject.next({
        ...subject.value,
        ...room,
      });
    }

    const updatedRooms = this.roomsSubject.value.map((r) =>
      r.id === room.id ? { ...r, ...room } : r,
    );

    if (!updatedRooms.some((r) => r.id === room.id)) {
      updatedRooms.unshift(room);
    }

    this.roomsSubject.next(this.sortRoomsByLastMessage([...updatedRooms]));
  }

  getRoomsForUser(userId: number): Observable<MessageRoomDto[]> {
    return this.api
      .Get<MessageRoomDto[]>(this.roomRoutes.room.getCurrentRooms, {
        params: { id: userId },
      })
      .pipe(
        tap((rooms) => {
          this.roomsSubject.next(this.sortRoomsByLastMessage(rooms));
        }),
      );
  }

  addRoom(room: MessageRoomDto) {
    const current = this.roomsSubject.value;
    if (!current.find((r) => r.id === room.id)) {
      this.roomsSubject.next([room, ...current]);
    }
  }

  renameRoom(roomId: number, newName: string) {
    const updated = this.roomsSubject.value.map((r) =>
      r.id === roomId ? { ...r, name: newName } : r,
    );
    this.roomsSubject.next(updated);
  }

  updateRoomPreview(roomId: number, msg: MessageDto): void {
    const currentRooms = this.roomsSubject.value || [];
    const roomIndex = currentRooms.findIndex((r) => r.id === roomId);
    const updatedRooms = [...currentRooms];

    if (roomIndex === -1) {
      updatedRooms.push({
        id: roomId,
        name: msg.name ?? '',
        isGroup: false,
        participants: [],
        lastMessage: msg,
        admins: [],
      });
    } else {
      updatedRooms[roomIndex] = {
        ...updatedRooms[roomIndex],
        lastMessage: msg,
      };
    }

    this.roomsSubject.next(this.sortRoomsByLastMessage(updatedRooms));
  }

  private sortRoomsByLastMessage(rooms: MessageRoomDto[]): MessageRoomDto[] {
    return rooms.sort((a, b) => {
      const dateA = a.lastMessage?.created ? new Date(a.lastMessage.created).getTime() : 0;
      const dateB = b.lastMessage?.created ? new Date(b.lastMessage.created).getTime() : 0;
      return dateB - dateA;
    });
  }

  private navigateToRoom(roomId: number): void {
    this.router.navigate(['messages/room', roomId]);
  }

  private navigateToGroupRoom(roomId: number): void {
    this.router.navigate(['messages/group', roomId]);
  }
}
